// A04 — Admin User edit. Three cards: Profile / Reset Password / Change
// Permission. Self-protection: when :id === auth.uid(), hide Delete +
// permission flags. PATCH with privilege-state changes prompts ConfirmModal.

import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { apiFetch } from "@/lib/api";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useToast } from "@/components/Toast";
import { useAdminMe } from "@/hooks/useAdminMe";

interface AdminUserDetail {
  profile: {
    id: string;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
    username: string | null;
    email: string | null;
    phone: string | null;
    is_active: boolean;
    role: string;
  };
  admin_permissions: {
    can_manage_admins: boolean;
    can_manage_app_users: boolean;
    can_manage_jobs: boolean;
    can_send_push: boolean;
    can_view_reports: boolean;
    role_label: string | null;
  } | null;
}

interface FormState {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  is_active: boolean;
  can_manage_admins: boolean;
  can_manage_app_users: boolean;
  can_manage_jobs: boolean;
  can_send_push: boolean;
  can_view_reports: boolean;
  role_label: string;
}

export function AdminUserEdit() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: me } = useAdminMe();
  const isSelf = me?.profile?.id === id;
  const { show, ToastEl } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const detail = useQuery<AdminUserDetail>({
    queryKey: ["admin-users", id],
    queryFn: () => apiFetch<AdminUserDetail>(`/v1/admin/admin-users/${id}`),
    enabled: Boolean(id),
  });

  const [form, setForm] = useState<FormState | null>(null);

  // Migration 00011 added first_name/last_name. For records that pre-date
  // the migration the back-end backfill already split display_name on the
  // first space, so first/last are normally populated. Fall back to a
  // client-side split if the API returned only display_name (e.g. legacy
  // staging row).
  const splitFromDisplay = (full: string | null) => {
    if (!full) return { first: "", last: "" };
    const ix = full.indexOf(" ");
    if (ix === -1) return { first: full, last: "" };
    return { first: full.slice(0, ix), last: full.slice(ix + 1).trim() };
  };
  const initialFromDetail = (d: AdminUserDetail): FormState => {
    const p = d.profile;
    const ap = d.admin_permissions;
    const fallback = splitFromDisplay(p.display_name);
    return {
      username: p.username ?? "",
      first_name: p.first_name ?? fallback.first,
      last_name: p.last_name ?? fallback.last,
      email: p.email ?? "",
      phone: p.phone ?? "",
      is_active: Boolean(p.is_active),
      can_manage_admins: Boolean(ap?.can_manage_admins),
      can_manage_app_users: Boolean(ap?.can_manage_app_users),
      can_manage_jobs: Boolean(ap?.can_manage_jobs),
      can_send_push: Boolean(ap?.can_send_push),
      can_view_reports: Boolean(ap?.can_view_reports),
      role_label: ap?.role_label ?? "Event user",
    };
  };

  useEffect(() => {
    if (!detail.data) return;
    setForm(initialFromDetail(detail.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data]);

  const original = useMemo(
    () => (detail.data ? initialFromDetail(detail.data) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail.data],
  );

  const patch = useMutation({
    mutationFn: async () => {
      if (!form) return;
      // username is read-only on this form (locked after creation), so we
      // intentionally don't diff it into the patch body.
      const body: Record<string, unknown> = {};
      if (form.first_name !== original?.first_name)
        body.first_name = form.first_name;
      if (form.last_name !== original?.last_name)
        body.last_name = form.last_name;
      if (form.email !== original?.email) body.email = form.email;
      if (!isSelf && form.is_active !== original?.is_active)
        body.is_active = form.is_active;
      // Permission flags only when not self. `role_label` is a TOP-LEVEL field
      // on PatchAdminUserReq (sub-plan 07 §7.1 line 181) even though it lives
      // on the admin_permissions table — keep it out of the nested `permissions`
      // object so the server reads the canonical contract path.
      if (!isSelf) {
        if (form.role_label !== original?.role_label)
          body.role_label = form.role_label;
        const permPatch: Record<string, unknown> = {};
        if (form.can_manage_admins !== original?.can_manage_admins)
          permPatch.can_manage_admins = form.can_manage_admins;
        if (form.can_manage_app_users !== original?.can_manage_app_users)
          permPatch.can_manage_app_users = form.can_manage_app_users;
        if (form.can_manage_jobs !== original?.can_manage_jobs)
          permPatch.can_manage_jobs = form.can_manage_jobs;
        if (form.can_send_push !== original?.can_send_push)
          permPatch.can_send_push = form.can_send_push;
        if (form.can_view_reports !== original?.can_view_reports)
          permPatch.can_view_reports = form.can_view_reports;
        if (Object.keys(permPatch).length) body.permissions = permPatch;
      }
      if (Object.keys(body).length === 0) {
        show("No changes to save", "info");
        return;
      }
      await apiFetch(`/v1/admin/admin-users/${id}`, {
        method: "PATCH",
        body,
      });
    },
    onSuccess: () => {
      show("Profile updated", "success");
      detail.refetch();
      setConfirmOpen(false);
    },
    onError: (err) => {
      show(err instanceof Error ? err.message : "Update failed", "error");
      setConfirmOpen(false);
    },
  });

  // Inline password reset (TVP2 §9). Two flows:
  //   - Self  (isSelf): old + new + confirm → POST /me/password
  //   - Other admin   :       new + confirm → POST /:id/set-password
  const [pwd, setPwd] = useState({ old: "", next: "", confirm: "" });
  const pwdNonEmpty = pwd.next.length > 0 || pwd.confirm.length > 0 || pwd.old.length > 0;
  const pwdMatch = pwd.next === pwd.confirm;
  const pwdLongEnough = pwd.next.length >= 8;
  const pwdSelfReady = isSelf
    ? pwd.old.length > 0 && pwdLongEnough && pwdMatch
    : pwdLongEnough && pwdMatch;

  const changeOwnPassword = useMutation({
    mutationFn: () =>
      apiFetch(`/v1/admin/admin-users/me/password`, {
        method: "POST",
        body: { old_password: pwd.old, new_password: pwd.next },
      }),
    onSuccess: () => {
      show("Password updated", "success");
      setPwd({ old: "", next: "", confirm: "" });
    },
    onError: (err) => show(err instanceof Error ? err.message : "Failed", "error"),
  });
  const setOtherPassword = useMutation({
    mutationFn: () =>
      apiFetch(`/v1/admin/admin-users/${id}/set-password`, {
        method: "POST",
        body: { new_password: pwd.next },
      }),
    onSuccess: () => {
      show("Password updated", "success");
      setPwd({ old: "", next: "", confirm: "" });
    },
    onError: (err) => show(err instanceof Error ? err.message : "Failed", "error"),
  });
  const pwdPending = changeOwnPassword.isPending || setOtherPassword.isPending;
  const submitPassword = () => {
    if (!pwdSelfReady) return;
    if (isSelf) changeOwnPassword.mutate();
    else setOtherPassword.mutate();
  };

  const isPrivilegeChange = useMemo(() => {
    if (!form || !original || isSelf) return false;
    if (form.is_active !== original.is_active) return true;
    if (form.role_label !== original.role_label) return true;
    return (
      form.can_manage_admins !== original.can_manage_admins ||
      form.can_manage_app_users !== original.can_manage_app_users ||
      form.can_manage_jobs !== original.can_manage_jobs ||
      form.can_send_push !== original.can_send_push ||
      form.can_view_reports !== original.can_view_reports
    );
  }, [form, original, isSelf]);

  const handleSave = () => {
    if (!form) return;
    if (isPrivilegeChange) {
      setConfirmOpen(true);
    } else {
      patch.mutate();
    }
  };

  if (detail.isLoading || !form) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (detail.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load admin user</Typography>
      </Box>
    );
  }

  const displayName =
    [form.first_name, form.last_name].filter(Boolean).join(" ") || "this admin";

  // Card + button styles match TVP2 §9 admin pages: bordered Paper cards
  // (no MUI Card shadow), Bootstrap-style action buttons (Save = primary
  // blue, Reset = grey). Inputs use MUI's `standard` variant to match the
  // underlined look in the reference.
  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
    p: 3,
  };
  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
    mb: 2,
  } as const;
  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const primaryBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#E8E8E8",
      color: "rgba(255,255,255,0.85)",
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Profile card — fields use `standard` variant so the underline
            look matches the search panel and the TVP2 reference. */}
        <Paper elevation={0} sx={cardSx}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              variant="standard"
              fullWidth
              required
              label="User Name"
              helperText="Cannot be changed after creation"
              disabled
              value={form.username}
            />
            <TextField
              variant="standard"
              fullWidth
              required
              label="E-mail"
              type="email"
              inputProps={{ maxLength: 254 }}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              variant="standard"
              fullWidth
              required
              select
              label="Status"
              value={form.is_active ? "active" : "inactive"}
              disabled={isSelf}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_active: e.target.value === "active",
                })
              }
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <TextField
              variant="standard"
              fullWidth
              required
              select
              label="Role"
              value={form.role_label}
              disabled={isSelf}
              onChange={(e) => setForm({ ...form, role_label: e.target.value })}
            >
              <MenuItem value="Administrator">Administrator</MenuItem>
              <MenuItem value="Event user">Event User</MenuItem>
              <MenuItem value="Normal User">Normal User</MenuItem>
            </TextField>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              variant="standard"
              fullWidth
              required
              label="First Name"
              inputProps={{ maxLength: 80 }}
              value={form.first_name}
              onChange={(e) =>
                setForm({ ...form, first_name: e.target.value })
              }
            />
            <TextField
              variant="standard"
              fullWidth
              required
              label="Last Name"
              inputProps={{ maxLength: 80 }}
              value={form.last_name}
              onChange={(e) =>
                setForm({ ...form, last_name: e.target.value })
              }
            />
            <TextField
              variant="standard"
              fullWidth
              label="Phone"
              helperText="E.164 format e.g. +85255551234"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              disabled
            />
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              disabled={patch.isPending}
              sx={primaryBtn}
              onClick={handleSave}
            >
              Save
            </Button>
            <Button
              variant="contained"
              sx={greyBtn}
              onClick={() => original && setForm(original)}
            >
              Reset
            </Button>
          </Stack>
        </Paper>

        {/* Reset password card — inline reset (TVP2 §9). Self-edit
            requires Old + New + Confirm; other admins only New + Confirm
            because the caller's `can_manage_admins` permission already
            authorises the override. */}
        <Paper elevation={0} sx={cardSx}>
          <Typography sx={sectionHeading}>Reset User Password</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            {isSelf && (
              <TextField
                variant="standard"
                fullWidth
                required
                label="Old Password"
                type="password"
                InputLabelProps={{ shrink: true }}
                value={pwd.old}
                onChange={(e) => setPwd({ ...pwd, old: e.target.value })}
              />
            )}
            <TextField
              variant="standard"
              fullWidth
              required
              label="New Password"
              type="password"
              helperText="Minimum 8 characters"
              InputLabelProps={{ shrink: true }}
              value={pwd.next}
              onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
            />
            <TextField
              variant="standard"
              fullWidth
              required
              label="Confirm Password"
              type="password"
              error={pwdNonEmpty && !pwdMatch}
              helperText={
                pwdNonEmpty && !pwdMatch ? "Passwords don't match" : " "
              }
              InputLabelProps={{ shrink: true }}
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
            />
          </Box>
          <Button
            variant="contained"
            sx={primaryBtn}
            disabled={!pwdSelfReady || pwdPending}
            onClick={submitPassword}
          >
            Submit
          </Button>
        </Paper>

        {/* Permissions are role-driven (migration 00012). Per-user toggles
            previously lived here have moved to the Roles & Permissions
            page. Self-edit hides this card since the role is already
            disabled in the Profile section above. */}
        {!isSelf && (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={sectionHeading}>User Permissions</Typography>
            <Typography
              sx={{ mb: 2, color: tokens.colors.admin.textBody }}
            >
              Permissions are inherited from this user's role
              ({form.role_label || "—"}). Update the Role on the Profile
              card above to change what this user can do, or edit the
              role's permission set directly.
            </Typography>
            <Button
              variant="contained"
              sx={greyBtn}
              onClick={() => navigate("/app/admin-users/roles")}
            >
              Manage Roles & Permissions
            </Button>
          </Paper>
        )}
      </Stack>

      <ConfirmModal
        open={confirmOpen}
        title="Confirm permission change"
        body={`About to change permissions for ${displayName}. The user's access will be updated immediately. Continue?`}
        confirmLabel="Update"
        pending={patch.isPending}
        onConfirm={() => patch.mutate()}
        onCancel={() => setConfirmOpen(false)}
      />
      {ToastEl}
    </Box>
  );
}
